import type { StockPhotoId } from "@/data/stockPhotos";
import { AUTOMACAO_FROM } from "@/data/automacaoServices";

export interface Service {
  slug: string;
  svgKey: "website" | "bot" | "magnify";
  tag: string;
  title: string;
  description: string;
  fullDescription: string;
  features: string[];
  process: string[];
  priceNote: string;
  /** Linha curta de oferta no card da home ("a partir de …", "Sob consulta"). */
  offer: string;
  /** Página especializada da frente (âncora em /servicos ou rota própria). */
  href: string;
  /** Título curto usado nos cards de resumo (home, rodapé). */
  shortTitle: string;
  /** Promessa de uma frase para o card da home. */
  summary: string;
  cta: string;
  /** Destino do CTA em /servicos; default /contato. */
  ctaHref?: string;
  photo: StockPhotoId;
}

export const services: Service[] = [
  {
    slug: "presenca-digital",
    photo: "loja-online",
    svgKey: "website",
    tag: "Presença Digital",
    title: "Site e sistema que trazem cliente",
    description:
      "Seu site não pode ser cartão de visita parado. Construo presença digital que gera contato, organiza processo e vende todo dia.",
    fullDescription:
      "Desenvolvo sites institucionais, landing pages de vendas, e-commerces e sistemas web sob medida com foco em um único objetivo: transformar visita em contato e contato em cliente. Cada página é pensada pra aparecer bem no Google, carregar rápido e guiar quem chega até a ação que importa pro seu negócio.",
    features: [
      "Site que aparece no Google e atrai o cliente certo",
      "Página de vendas pensada para converter visita em contato",
      "Sistema sob medida para sair da planilha bagunçada",
      "Loja online pronta para receber pedido com segurança",
    ],
    process: [
      "Diagnóstico do objetivo do site e do público que ele precisa atrair",
      "Estrutura e design focados em conversão, não só em estética",
      "Desenvolvimento com tecnologia moderna, rápida e fácil de manter",
      "Testes, ajustes finos e publicação com acompanhamento pós-entrega",
    ],
    priceNote:
      "Sob consulta — o investimento depende do tamanho do projeto. A proposta sai depois de entender seu objetivo.",
    offer: "Proposta após diagnóstico",
    href: "/servicos#presenca-digital",
    shortTitle: "Sites e sistemas",
    summary:
      "Site que aparece no Google e gera contato, loja online e sistema sob medida para sair da planilha.",
    cta: "Quero um site que vende",
  },
  {
    slug: "automacao-ia",
    photo: "pedido-fechado",
    svgKey: "bot",
    tag: "Automação com IA",
    title: "Automatize o que rouba o tempo da sua equipe",
    description:
      "Responder o mesmo WhatsApp, copiar dado entre sistemas, atender fora do horário. Tudo isso pode rodar sozinho — com IA fazendo o trabalho pesado.",
    fullDescription:
      "Construo atendentes virtuais, integrações entre ferramentas e automações com inteligência artificial que assumem as tarefas repetitivas do dia a dia. O objetivo é liberar sua equipe pra focar no que realmente precisa de gente — enquanto o sistema cuida do resto, 24 horas por dia.",
    features: [
      "Atendente virtual que responde cliente 24 horas",
      "Conectar suas ferramentas para acabar com retrabalho",
      "Envio automático de e-mail, mensagem e tarefa",
      "IA que organiza, classifica e responde com a voz da empresa",
    ],
    process: [
      "Mapeamento das tarefas manuais que mais consomem tempo da equipe",
      "Desenho do fluxo de automação e das ferramentas que serão integradas",
      "Implementação e treinamento da IA com a linguagem do seu negócio",
      "Acompanhamento dos primeiros ciclos para ajustar respostas e regras",
    ],
    priceNote: `A partir de ${AUTOMACAO_FROM} — CRM e atendimento com planos fechados. A faixa exata sai do diagnóstico gratuito.`,
    offer: `a partir de ${AUTOMACAO_FROM}`,
    href: "/automacao-empresarial",
    shortTitle: "Automação com IA",
    summary:
      "CRM que faz follow-up sozinho e atendimento que responde WhatsApp, Instagram e site 24 horas.",
    cta: "Ver planos de automação",
    ctaHref: "/automacao-empresarial",
  },
  {
    slug: "consultoria",
    photo: "reuniao-equipe",
    svgKey: "magnify",
    tag: "Consultoria",
    title: "Descubra onde seu processo está vazando dinheiro",
    description:
      "Antes de comprar sistema, vale entender onde o tempo e o dinheiro se perdem hoje. Faço esse mapa com você.",
    fullDescription:
      "Ofereço consultoria técnica independente pra quem precisa decidir onde investir em tecnologia sem cair em venda forçada. Analiso o que já existe no seu negócio, aponto o que está funcionando, o que está travando e qual é o caminho mais barato e realista pra resolver.",
    features: [
      "Mapa dos gargalos que mais custam tempo hoje",
      "Prioridade do que automatizar primeiro",
      "Estimativa de retorno antes de investir",
      "Plano em etapas, sem projeto de 6 meses",
    ],
    process: [
      "Conversa inicial gratuita para entender o cenário atual",
      "Levantamento dos processos, sistemas e custos envolvidos",
      "Relatório com diagnóstico claro e recomendações priorizadas",
      "Apoio na execução, se você quiser seguir junto comigo",
    ],
    priceNote:
      "O diagnóstico de 30 minutos é gratuito. A consultoria aprofundada (levantamento + relatório) é sob consulta.",
    offer: "Sob consulta",
    href: "/servicos#consultoria",
    shortTitle: "Consultoria",
    summary:
      "Mapa de onde seu processo perde tempo e dinheiro, com prioridade e estimativa de retorno antes de investir.",
    cta: "Quero mapear meu processo",
  },
];

/** Passos comuns às três frentes (home). O hub de automação usa `automacaoSteps`. */
export const workSteps = [
  {
    n: "1",
    title: "Diagnóstico gratuito",
    desc: "30 minutos olhando seu processo real. Você sai sabendo o que vale fazer primeiro.",
    when: "Dia 1",
  },
  {
    n: "2",
    title: "Proposta fechada",
    desc: "Escopo, prazo e valor por escrito. Sem surpresa no meio do projeto.",
    when: "Até 3 dias",
  },
  {
    n: "3",
    title: "Entrega em etapas",
    desc: "Algo funcionando no fim de cada etapa, nada de projeto de 6 meses.",
    when: "Semanas, não meses",
  },
  {
    n: "4",
    title: "Treino e ajuste",
    desc: "Equipe treinada e primeiros ciclos acompanhados de perto.",
    when: "Pós-entrega",
  },
];
